import { useState, useMemo, useEffect, useRef } from 'react';
import { useDynamicTableHeight } from '@/hooks/use-dynamic-table-height';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronRight, ChevronDown, FolderOpen, Folder, FileText, ChevronLeft } from 'lucide-react';
import { formatCurrency, formatHours, formatPercent, getVarianceColor } from '@/lib/budget-utils';
import { cn } from '@/lib/utils';
import { getStatusColor } from '@/lib/status-colors';
import type { CostCentre } from './cost-centres-table';
import type { CostCentreColumnDefinition } from './cost-centres-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export interface HierarchicalCostCentre extends CostCentre {
  parentId?: number | null;
  children?: HierarchicalCostCentre[];
  level?: number;
}

interface CostCentresHierarchicalTableProps {
  data: HierarchicalCostCentre[];
  columns?: CostCentreColumnDefinition[];
  onRowClick?: (costCentre: HierarchicalCostCentre) => void;
  loading?: boolean;
  useCardWrapper?: boolean; // Feature flag to enable/disable card wrapper
}

interface FlattenedNode extends HierarchicalCostCentre {
  level: number;
  hasChildren: boolean;
  isExpanded: boolean;
}

export function CostCentresHierarchicalTable({ 
  data,
  columns = [],
  onRowClick,
  loading = false,
  useCardWrapper = true, // Default to true for new behaviour
}: CostCentresHierarchicalTableProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Dynamic height calculation
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const dynamicHeight = useDynamicTableHeight(tableContainerRef, {
    minHeight: 400,
    bottomPadding: 24,
  });
  
  // Reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [data.length]);

  // Build tree structure from flat data
  const treeData = useMemo(() => {
    const buildTree = (items: HierarchicalCostCentre[]): HierarchicalCostCentre[] => {
      const map = new Map<number, HierarchicalCostCentre>();
      const roots: HierarchicalCostCentre[] = [];

      // First pass: create map of all items
      items.forEach(item => {
        map.set(item.id, { ...item, children: [], level: 0 });
      });

      // Second pass: build tree structure
      items.forEach(item => {
        const node = map.get(item.id)!;
        if (item.parentId !== undefined && item.parentId !== null && map.has(item.parentId)) {
          const parent = map.get(item.parentId)!;
          if (!parent.children) parent.children = [];
          parent.children.push(node);
          node.level = (parent.level || 0) + 1;
        } else {
          roots.push(node);
        }
      });

      return roots;
    };

    return buildTree(data);
  }, [data]);

  // Calculate aggregated totals for parent nodes
  const calculateAggregates = (node: HierarchicalCostCentre): HierarchicalCostCentre => {
    if (!node.children || node.children.length === 0) {
      return node;
    }

    // Recursively calculate for children first
    const calculatedChildren = node.children.map(calculateAggregates);

    // Aggregate values from children
    const aggregated = calculatedChildren.reduce((acc, child) => ({
      costsBudget: acc.costsBudget + child.costsBudget,
      actualCosts: acc.actualCosts + child.actualCosts,
      committedCosts: acc.committedCosts + child.committedCosts,
      revenueBudget: acc.revenueBudget + child.revenueBudget,
      actualRevenue: acc.actualRevenue + child.actualRevenue,
      hoursBudget: acc.hoursBudget + child.hoursBudget,
      actualHours: acc.actualHours + child.actualHours,
    }), {
      costsBudget: 0,
      actualCosts: 0,
      committedCosts: 0,
      revenueBudget: 0,
      actualRevenue: 0,
      hoursBudget: 0,
      actualHours: 0,
    });

    return {
      ...node,
      ...aggregated,
      children: calculatedChildren,
    };
  };

  const aggregatedTreeData = useMemo(() => {
    return treeData.map(calculateAggregates);
  }, [treeData]);

  // Flatten tree for rendering
  const flattenedData = useMemo(() => {
    const flatten = (nodes: HierarchicalCostCentre[], level = 0): FlattenedNode[] => {
      const result: FlattenedNode[] = [];
      
      nodes.forEach(node => {
        const hasChildren = !!(node.children && node.children.length > 0);
        const isExpanded = expandedNodes.has(node.id);
        
        result.push({
          ...node,
          level,
          hasChildren,
          isExpanded,
        });

        if (hasChildren && isExpanded) {
          result.push(...flatten(node.children!, level + 1));
        }
      });

      return result;
    };

    return flatten(aggregatedTreeData);
  }, [aggregatedTreeData, expandedNodes]);

  // Pagination calculations
  const totalItems = flattenedData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = flattenedData.slice(startIndex, endIndex);

  // Pagination handlers
  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize));
    setCurrentPage(1);
  };

  // Toggle node expansion
  const toggleNode = (nodeId: number) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  // Expand all nodes
  const expandAll = () => {
    const allIds = new Set<number>();
    const collectIds = (nodes: HierarchicalCostCentre[]) => {
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          allIds.add(node.id);
          collectIds(node.children);
        }
      });
    };
    collectIds(aggregatedTreeData);
    setExpandedNodes(allIds);
  };

  // Collapse all nodes
  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  // Calculate derived values
  const calculateTotalCosts = (row: HierarchicalCostCentre) => row.actualCosts + row.committedCosts;
  const calculateCostVariance = (row: HierarchicalCostCentre) => row.costsBudget - row.actualCosts;
  const calculateRevenueVariance = (row: HierarchicalCostCentre) => row.revenueBudget - row.actualRevenue;
  const calculateHoursVariance = (row: HierarchicalCostCentre) => row.hoursBudget - row.actualHours;
  const calculateCostPercent = (row: HierarchicalCostCentre) => row.costsBudget > 0 ? (row.actualCosts / row.costsBudget) * 100 : 0;
  const calculateRevenuePercent = (row: HierarchicalCostCentre) => row.revenueBudget > 0 ? (row.actualRevenue / row.revenueBudget) * 100 : 0;
  const calculateHoursPercent = (row: HierarchicalCostCentre) => row.hoursBudget > 0 ? (row.actualHours / row.hoursBudget) * 100 : 0;
  const calculateRemainingCosts = (row: HierarchicalCostCentre) => row.costsBudget - row.actualCosts;
  const calculateRemainingRevenue = (row: HierarchicalCostCentre) => row.revenueBudget - row.actualRevenue;
  const calculateRemainingHours = (row: HierarchicalCostCentre) => row.hoursBudget - row.actualHours;
  const calculateForecastCosts = (row: HierarchicalCostCentre) => {
    const percent = calculateCostPercent(row);
    return percent > 0 ? (row.actualCosts / percent) * 100 : row.actualCosts;
  };
  const calculateForecastRevenue = (row: HierarchicalCostCentre) => {
    const percent = calculateRevenuePercent(row);
    return percent > 0 ? (row.actualRevenue / percent) * 100 : row.actualRevenue;
  };

  // Get value for display
  const getCellValue = (row: HierarchicalCostCentre, key: string): number | string | boolean => {
    switch (key) {
      case 'totalCosts': return calculateTotalCosts(row);
      case 'costVariance': return calculateCostVariance(row);
      case 'revenueVariance': return calculateRevenueVariance(row);
      case 'hoursVariance': return calculateHoursVariance(row);
      case 'costPercent': return calculateCostPercent(row);
      case 'revenuePercent': return calculateRevenuePercent(row);
      case 'hoursPercent': return calculateHoursPercent(row);
      case 'remainingCosts': return calculateRemainingCosts(row);
      case 'remainingRevenue': return calculateRemainingRevenue(row);
      case 'remainingHours': return calculateRemainingHours(row);
      case 'forecastCosts': return calculateForecastCosts(row);
      case 'forecastRevenue': return calculateForecastRevenue(row);
      default: return row[key as keyof HierarchicalCostCentre] as number | string | boolean;
    }
  };

  // Render cell content
  const renderCell = (row: FlattenedNode, column: CostCentreColumnDefinition, isFirstColumn: boolean) => {
    const value = getCellValue(row, column.key);

    // Special handling for first visible column (name or code)
    if (isFirstColumn) {
      const indentWidth = row.level * 24; // 24px per level
      
      return (
        <div className="flex items-center gap-2" style={{ paddingLeft: `${indentWidth}px` }}>
          {row.hasChildren ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 hover:bg-muted"
              onClick={() => toggleNode(row.id)}
            >
              {row.isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          ) : (
            <div className="w-6" />
          )}
          
          {row.hasChildren ? (
            row.isExpanded ? (
              <FolderOpen className="h-4 w-4 text-info-500 flex-shrink-0" />
            ) : (
              <Folder className="h-4 w-4 text-info-500 flex-shrink-0" />
            )
          ) : (
            <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          )}
          
          <span className={cn(
            row.hasChildren && 'font-semibold'
          )}>
            {value?.toString() || '-'}
          </span>
        </div>
      );
    }

    // Regular cell rendering
    switch (column.key) {
      case 'status':
        // Parent nodes don't have status
        if (row.hasChildren) return '-';
        
        const statusValue = value as string;
        return (
          <Badge className={getStatusColor(statusValue)}>
            {statusValue || '-'}
          </Badge>
        );
      
      case 'billable':
        // Parent nodes don't have billable status
        if (row.hasChildren) return '-';
        return (
          <div className="flex justify-center">
            <Badge variant={value ? 'default' : 'secondary'}>
              {value ? 'Yes' : 'No'}
            </Badge>
          </div>
        );
      
      case 'isActive':
        // Parent nodes don't have active status
        if (row.hasChildren) return '-';
        return (
          <div className="flex justify-center">
            <Badge variant={value !== false ? 'default' : 'secondary'}>
              {value !== false ? 'Yes' : 'No'}
            </Badge>
          </div>
        );
      
      case 'costsBudget':
      case 'actualCosts':
      case 'committedCosts':
      case 'totalCosts':
      case 'revenueBudget':
      case 'actualRevenue':
      case 'remainingCosts':
      case 'remainingRevenue':
      case 'forecastCosts':
      case 'forecastRevenue':
        return (
          <span className={cn(row.hasChildren && 'font-semibold')}>
            {formatCurrency(value as number)}
          </span>
        );
      
      case 'costVariance':
      case 'revenueVariance':
        return (
          <span className={cn(
            getVarianceColor(value as number),
            row.hasChildren && 'font-semibold'
          )}>
            {value as number > 0 ? '+' : ''}{formatCurrency(value as number)}
          </span>
        );
      
      case 'hoursVariance':
        return (
          <span className={cn(
            getVarianceColor(value as number),
            row.hasChildren && 'font-semibold'
          )}>
            {value as number > 0 ? '+' : ''}{formatHours(value as number)}
          </span>
        );
      
      case 'costPercent':
      case 'revenuePercent':
      case 'hoursPercent':
        const percent = value as number;
        const isOverBudget = percent > 100;
        return (
          <div className="flex items-center gap-2">
            <span className={cn(
              isOverBudget && 'text-error-600 dark:text-error-500',
              row.hasChildren && 'font-semibold'
            )}>
              {formatPercent(percent)}
            </span>
            {isOverBudget && !row.hasChildren && (
              <Badge variant="destructive" className="text-xs">
                Over
              </Badge>
            )}
          </div>
        );
      
      case 'hoursBudget':
      case 'actualHours':
      case 'remainingHours':
        return (
          <span className={cn(row.hasChildren && 'font-semibold')}>
            {formatHours(value as number)}
          </span>
        );
      
      case 'type':
        // Parent nodes might not have types
        if (row.hasChildren && !value) return '-';
        return value?.toString() || '-';
      
      case 'owner':
        // Parent nodes don't have owners
        if (row.hasChildren) return '-';
        return value?.toString() || '-';
      
      case 'lastUpdated':
        // Parent nodes don't have last updated
        if (row.hasChildren) return '-';
        return value ? new Date(value as string | Date).toLocaleDateString('en-GB') : '-';
      
      default:
        return value?.toString() || '-';
    }
  };

  const visibleColumns = columns.filter(col => col.visible);
  const firstVisibleColumn = visibleColumns[0];

  // Define pinned columns (Code, Name, Type, Status)
  const pinnedColumnKeys = ['code', 'name', 'type', 'status'];
  
  // Helper to check if column is pinned
  const isPinnedColumn = (columnKey: string) => pinnedColumnKeys.includes(columnKey);
  
  // Get sticky column class based on column key
  const getStickyColumnClass = (columnKey: string, isParentRow: boolean = false) => {
    const baseClass = isParentRow ? 'bg-muted' : 'bg-background';
    switch (columnKey) {
      case 'code':
        return `sticky left-0 z-10 ${baseClass}`;
      case 'name':
        return `sticky left-[120px] z-10 ${baseClass}`;
      case 'type':
        return `sticky left-[400px] z-10 ${baseClass}`;
      case 'status':
        return `sticky left-[520px] z-10 ${baseClass} shadow-[2px_0_8px_-2px_rgba(0,0,0,0.1)]`;
      default:
        return '';
    }
  };
  
  // Get column width class
  const getColumnWidthClass = (columnKey: string) => {
    switch (columnKey) {
      case 'code':
        return 'w-[120px] min-w-[120px]';
      case 'name':
        return 'w-[280px] min-w-[280px]'; // Wider for hierarchical indentation
      case 'type':
        return 'w-[120px] min-w-[120px]';
      case 'status':
        return 'w-[150px] min-w-[150px]';
      default:
        return '';
    }
  };

  // Determine if a column is the first pinned column (for hierarchical rendering)
  const isFirstPinnedColumn = (columnKey: string) => {
    return columnKey === 'code';
  };

  // Control buttons component
  const controlButtons = (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={expandAll}
        disabled={loading}
      >
        Expand All
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={collapseAll}
        disabled={loading}
      >
        Collapse All
      </Button>
    </div>
  );

  // Table content component
  const tableContent = (
    <div 
      ref={tableContainerRef}
      className="rounded-md border overflow-auto"
    >
      <Table className="w-max min-w-full">
        <TableHeader className="sticky top-0 z-30 bg-background">
          <TableRow>
            {visibleColumns.map((column) => (
              <TableHead
                key={column.key}
                className={cn(
                  'py-3 bg-background',
                  column.align === 'right' && 'text-right',
                  column.align === 'center' && 'text-center',
                  getStickyColumnClass(column.key, false),
                  getColumnWidthClass(column.key),
                  isPinnedColumn(column.key) && 'z-40'
                )}
              >
                {column.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            // Loading skeleton
            Array.from({ length: pageSize }).map((_, i) => (
              <TableRow key={i}>
                {visibleColumns.map((column) => (
                  <TableCell
                    key={column.key}
                    className={cn(
                      'py-3',
                      getStickyColumnClass(column.key, false),
                      getColumnWidthClass(column.key)
                    )}
                  >
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : totalItems === 0 ? (
            // Empty state
            <TableRow>
              <TableCell 
                colSpan={visibleColumns.length} 
                className="text-center text-muted-foreground py-12"
              >
                <div className="flex flex-col items-center gap-2">
                  <p className="text-lg font-medium">No cost centres found</p>
                  <p className="text-sm">Get started by creating your first cost centre or configure the structure</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            paginatedData.map((row) => (
              <TableRow 
                key={row.id}
                className={cn(
                  row.hasChildren && 'bg-muted/30 hover:bg-muted/50',
                  !row.hasChildren && onRowClick && 'cursor-pointer hover:bg-muted/50'
                )}
                onClick={() => {
                  // Only trigger row click for leaf nodes (actual cost centres), not parent nodes
                  if (!row.hasChildren && onRowClick) {
                    onRowClick(row);
                  }
                }}
              >
                {visibleColumns.map((column) => (
                  <TableCell
                    key={column.key}
                    className={cn(
                      'py-3',
                      column.align === 'right' && 'text-right',
                      column.align === 'center' && 'text-center',
                      getStickyColumnClass(column.key, row.hasChildren),
                      getColumnWidthClass(column.key)
                    )}
                  >
                    {renderCell(row, column, isFirstPinnedColumn(column.key))}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  // Pagination controls
  const paginationControls = totalItems > 0 && (
    <div className="flex items-center justify-between gap-4 px-2">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">
          Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} cost centres
        </span>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Rows per page:</span>
          <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1 || loading}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages || loading}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  // Return with or without Card wrapper based on feature flag
  if (useCardWrapper) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              {totalItems} Cost Centre{totalItems !== 1 ? 's' : ''}
            </CardTitle>
            {controlButtons}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {tableContent}
          {paginationControls}
        </CardContent>
      </Card>
    );
  }

  // Legacy layout without Card wrapper
  return (
    <div className="space-y-3">
      {controlButtons}
      {tableContent}
      {paginationControls}
    </div>
  );
}
